// Pure helpers for reasoning about a fulfillment method's calculated slot list.
// Callers inject a resolver so these stay testable without Vuex.

export function hasFutureSlots(calculatedFulfillmentSlots) {
  if (!Array.isArray(calculatedFulfillmentSlots)) return false
  return calculatedFulfillmentSlots.some((slot) => slot?.hasnt_started_yet === true)
}

// True if any slot is currently orderable by the customer (can_select === true).
export function hasSelectableSlots(calculatedFulfillmentSlots) {
  if (!Array.isArray(calculatedFulfillmentSlots)) return false
  return calculatedFulfillmentSlots.some((slot) => slot?.can_select === true)
}

// True if any slot represents an option the customer can engage with — either
// pickable now (can_select) or upcoming and not-yet-open (hasnt_started_yet).
// Used to hide methods that have nothing to offer the customer at all.
export function hasOrderableOrUpcomingSlots(calculatedFulfillmentSlots) {
  if (!Array.isArray(calculatedFulfillmentSlots)) return false
  return calculatedFulfillmentSlots.some((slot) => slot?.can_select === true || slot?.hasnt_started_yet === true)
}

// method: a JSONAPI fulfillment_method resource (has relationships.fulfillment_slots_detail.data).
// resolveRelationship: (stub) => resolved resource — typically a closure over
//   (stub) => mapRelationship(this.$store.state, stub)
function calculatedSlotsForMethod(method, resolveRelationship) {
  const stub = method?.relationships?.fulfillment_slots_detail?.data
  if (!stub) return []
  return resolveRelationship(stub)?.attributes?.calculated_fulfillment_slots || []
}

export function methodHasFutureSlots(method, resolveRelationship) {
  return hasFutureSlots(calculatedSlotsForMethod(method, resolveRelationship))
}

export function methodHasSelectableSlots(method, resolveRelationship) {
  return hasSelectableSlots(calculatedSlotsForMethod(method, resolveRelationship))
}

export function methodHasOrderableOrUpcomingSlots(method, resolveRelationship) {
  return hasOrderableOrUpcomingSlots(calculatedSlotsForMethod(method, resolveRelationship))
}

// How many dates on this list the customer can actually pick right now.
export function selectableSlotCount(calculatedFulfillmentSlots) {
  if (!Array.isArray(calculatedFulfillmentSlots)) return 0
  return calculatedFulfillmentSlots.filter((slot) => slot?.can_select === true).length
}

export function methodSelectableSlotCount(method, resolveRelationship) {
  return selectableSlotCount(calculatedSlotsForMethod(method, resolveRelationship))
}

// The methods this address can actually be offered: eligible for the address, and
// carrying at least one slot the customer could engage with. Shared so the checkout
// and the selected-fulfillment module agree on the option list without either one
// having to wait on the other to render.
// resolveMethods: (stubs) => resolved method resources, typically the Vuex
//   `fulfillmentMethods` getter.
export function offerableMethodsForAddress(deliveryAddress, resolveMethods, resolveRelationship) {
  const stubs = deliveryAddress?.relationships?.eligible_fulfillment_methods?.data
  return resolveMethods(stubs).filter((method) => methodHasOrderableOrUpcomingSlots(method, resolveRelationship))
}

// True when the customer genuinely has something to choose between: more than one
// eligible method, or a single method offering more than one selectable date.
// BOT-1545 uses this so the checkout only asks for a slot confirmation when the
// backend's auto-assignment actually took a decision away from the customer.
export function hasFulfillmentChoice(methods, resolveRelationship) {
  if (!Array.isArray(methods) || methods.length === 0) return false
  if (methods.length > 1) return true
  return methodSelectableSlotCount(methods[0], resolveRelationship) > 1
}

function methodPriceCents(method) {
  return method?.attributes?.price?.cents || 0
}

export function methodPriceLabel(method) {
  return methodPriceCents(method) > 0 ? method.attributes.price.format : 'Free'
}

function earliestOpening(options) {
  return options.reduce((earliest, option) =>
    new Date(option.will_open_datetime) < new Date(earliest.will_open_datetime) ? option : earliest
  )
}

function unselectableDateOption(options) {
  const upcoming = options.filter((option) => option.hasnt_started_yet)
  if (upcoming.length > 0) return earliestOpening(upcoming)
  return ['sold_out', 'has_ended', 'closed'].map((status) => options.find((option) => option[status])).find(Boolean)
}

function datePriceLabel(selectableByPrice) {
  const lowest = methodPriceLabel(selectableByPrice[0].method)
  const feeCount = new Set(selectableByPrice.map(({ method }) => methodPriceCents(method))).size
  return feeCount > 1 ? `from ${lowest}` : lowest
}

export function mergedSlotsAcrossMethods(methods, resolveRelationship, currentMethodId) {
  if (!Array.isArray(methods)) return []
  const feesDiffer = new Set(methods.map(methodPriceCents)).size > 1
  const entriesByDate = new Map()
  methods.forEach((method) => {
    calculatedSlotsForMethod(method, resolveRelationship).forEach((option) => {
      if (!entriesByDate.has(option.date)) entriesByDate.set(option.date, [])
      entriesByDate.get(option.date).push({ method, option })
    })
  })

  return [...entriesByDate.keys()].sort().map((date) => {
    const entries = entriesByDate.get(date)
    const selectable = entries.filter(({ option }) => option.can_select)
    if (selectable.length === 0) return unselectableDateOption(entries.map(({ option }) => option))

    const selectableByPrice = [...selectable].sort((a, b) => methodPriceCents(a.method) - methodPriceCents(b.method))
    const chosen = selectable.find(({ method }) => method.id == currentMethodId) || selectableByPrice[0]
    return feesDiffer ? { ...chosen.option, price_label: datePriceLabel(selectableByPrice) } : { ...chosen.option }
  })
}

export function selectableSlotIdOnDate(method, resolveRelationship, date) {
  const option = calculatedSlotsForMethod(method, resolveRelationship).find(
    (slot) => slot.date == date && slot.can_select
  )
  return option?.fulfillment_slot_id ?? null
}

export function methodsWithSelectableSlotOn(methods, resolveRelationship, date) {
  return (methods || []).filter((method) => selectableSlotIdOnDate(method, resolveRelationship, date) != null)
}
